// Auth endpoint wrappers. Each function returns the parsed response or throws
// an ApiError that callers can introspect (status, code, details).

import { apiRequest } from "./client";
import type { AuthResponse, UserWithWallet } from "./types";

export interface Credentials {
  email: string;
  password: string;
}

export function signup(creds: Credentials): Promise<AuthResponse> {
  return apiRequest<AuthResponse>("/auth/signup", {
    method: "POST",
    json: creds,
    skipAuth: true,
  });
}

export function login(creds: Credentials): Promise<AuthResponse> {
  return apiRequest<AuthResponse>("/auth/login", {
    method: "POST",
    json: creds,
    skipAuth: true,
  });
}

export function logout(): Promise<void> {
  return apiRequest<void>("/auth/logout", {
    method: "POST",
  });
}

export function getMe(): Promise<UserWithWallet> {
  return apiRequest<UserWithWallet>("/me");
}

export function verifyEmail(token: string): Promise<{ ok: boolean; message: string }> {
  return apiRequest<{ ok: boolean; message: string }>(
    `/auth/verify-email?token=${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function resendVerification(): Promise<{ ok: boolean; message: string }> {
  return apiRequest<{ ok: boolean; message: string }>("/auth/resend-verification", {
    method: "POST",
  });
}

export function forgotPassword(email: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>("/auth/forgot-password", {
    method: "POST",
    json: { email },
    skipAuth: true,
  });
}

export function resetPassword(
  token: string,
  password: string,
): Promise<{ ok: boolean; message: string }> {
  return apiRequest<{ ok: boolean; message: string }>("/auth/reset-password", {
    method: "POST",
    json: { token, password },
    skipAuth: true,
  });
}

export function cancelDeletion(
  token: string,
): Promise<{ ok: boolean; code: string; message: string }> {
  return apiRequest<{ ok: boolean; code: string; message: string }>(
    `/auth/cancel-deletion?token=${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function getGoogleAuthUrl(): Promise<{ url: string; state: string }> {
  return apiRequest<{ url: string; state: string }>("/auth/google", { skipAuth: true });
}

export function googleCallback(code: string, state: string): Promise<AuthResponse> {
  return apiRequest<AuthResponse>(
    `/auth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    { skipAuth: true },
  );
}

export function getMicrosoftAuthUrl(): Promise<{ url: string; state: string }> {
  return apiRequest<{ url: string; state: string }>("/auth/microsoft", { skipAuth: true });
}

export function microsoftCallback(code: string, state: string): Promise<AuthResponse> {
  return apiRequest<AuthResponse>(
    `/auth/microsoft/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    { skipAuth: true },
  );
}
